import { buildSearchText } from "@nomnom/core";
import type { WaitlistCampaign, WaitlistEntry, WaitlistRole, WaitlistStatus } from "@nomnom/core";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  doc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../config/firebase";

export type WaitlistRoleFilter = "all" | WaitlistRole;
export type WaitlistStatusFilter = "all" | WaitlistStatus;

export type WaitlistInvitePayload = {
  role: WaitlistRole;
  entryIds: string[];
  campaignLabel: string;
  ctaUrl: string;
  customNote?: string;
};

export const WAITLIST_INVITE_URLS: Record<WaitlistRole, string> = {
  student: "https://www.nomnom.cc/app",
  rider: "https://www.nomnom.cc/rider",
};

export type WaitlistInviteResult = {
  campaignId: string;
  sentCount: number;
  failedCount: number;
  failures: Array<{
    entryId: string;
    email: string;
    reason: string;
  }>;
};

export type WaitlistMetric = {
  label: string;
  value: number;
};

export type WaitlistLaunchMetrics = {
  activeStudents: number;
  activeRiders: number;
  confirmedStudents: number;
  confirmedRiders: number;
  inviteLocked: boolean;
  recommendedStudentCohortSize: number;
  selectedReadyCount: number;
};

type TimestampLike = {
  toDate: () => Date;
};

function getTimestampValue(value: unknown): number {
  if (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof (value as TimestampLike).toDate === "function"
  ) {
    return (value as TimestampLike).toDate().getTime();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  return 0;
}

function sortWaitlistCampaigns(campaigns: WaitlistCampaign[]): WaitlistCampaign[] {
  return [...campaigns].sort((left, right) => {
    const leftTime = Math.max(
      getTimestampValue(left.createdAt),
      getTimestampValue(left.requestedAt),
    );
    const rightTime = Math.max(
      getTimestampValue(right.createdAt),
      getTimestampValue(right.requestedAt),
    );

    return rightTime - leftTime;
  });
}

export function subscribeToWaitlistEntries(
  onUpdate: (entries: WaitlistEntry[]) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    query(collection(db, "waitlistEntries"), orderBy("createdAt", "desc"), limit(500)),
    (snapshot) => {
      onUpdate(
        snapshot.docs.map(
          (docSnap) => ({ id: docSnap.id, ...docSnap.data() }) as WaitlistEntry,
        ),
      );
    },
    (error) => {
      console.error("[WaitlistService] Failed to load waitlist entries:", error);
      onError("Failed to load waitlist entries.");
    },
  );
}

export function subscribeToWaitlistCampaigns(
  onUpdate: (campaigns: WaitlistCampaign[]) => void,
  onError: (message: string) => void,
): () => void {
  let unsubscribe: () => void = () => {};

  const handleSnapshot = (snapshot: {
    docs: Array<{ id: string; data: () => Record<string, unknown> }>;
  }) => {
    onUpdate(
      sortWaitlistCampaigns(
        snapshot.docs.map(
          (docSnap) => ({ id: docSnap.id, ...docSnap.data() }) as WaitlistCampaign,
        ),
      ),
    );
  };

  const fallbackQuery = query(collection(db, "waitlistCampaigns"), limit(100));
  const orderedQuery = query(
    collection(db, "waitlistCampaigns"),
    orderBy("createdAt", "desc"),
    limit(100),
  );

  unsubscribe = onSnapshot(
    orderedQuery,
    handleSnapshot,
    (error) => {
      console.error("[WaitlistService] Failed ordered waitlist campaign query:", error);

      unsubscribe();
      unsubscribe = onSnapshot(
        fallbackQuery,
        handleSnapshot,
        (fallbackError) => {
          console.error("[WaitlistService] Failed to load waitlist campaigns:", fallbackError);
          onError("Failed to load waitlist campaign history.");
        },
      );
    },
  );

  return () => {
    unsubscribe();
  };
}

export function filterWaitlistEntries(
  entries: WaitlistEntry[],
  roleFilter: WaitlistRoleFilter,
  statusFilter: WaitlistStatusFilter,
  search: string,
): WaitlistEntry[] {
  const normalizedSearch = search.trim().toLowerCase();

  return entries.filter((entry) => {
    if (roleFilter !== "all" && entry.role !== roleFilter) {
      return false;
    }

    if (statusFilter !== "all" && entry.status !== statusFilter) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    return buildSearchText([
      entry.fullName,
      entry.email,
      entry.heardAboutNomNom,
      entry.notes,
      entry.studentProfile?.dormOrArea,
      entry.riderProfile?.transportMethod,
      entry.riderProfile?.typicalAvailability,
    ]).includes(normalizedSearch);
  });
}

export function buildWaitlistMetrics(entries: WaitlistEntry[]): WaitlistMetric[] {
  const active = entries.filter((entry) => entry.status === "active").length;
  const invited = entries.filter((entry) => entry.status === "invited").length;
  const students = entries.filter((entry) => entry.role === "student").length;
  const riders = entries.filter((entry) => entry.role === "rider").length;

  return [
    { label: "Active", value: active },
    { label: "Invited", value: invited },
    { label: "Students", value: students },
    { label: "Riders", value: riders },
  ];
}

export function buildWaitlistLaunchMetrics(
  entries: WaitlistEntry[],
  selectedEntries: WaitlistEntry[],
  liveInvitesEnabled: boolean,
): WaitlistLaunchMetrics {
  const isConfirmed = (entry: WaitlistEntry) =>
    entry.confirmationDelivery?.state === "sent";
  const activeEntries = entries.filter((entry) => entry.status === "active");
  const activeStudents = activeEntries.filter((entry) => entry.role === "student");
  const activeRiders = activeEntries.filter((entry) => entry.role === "rider");
  const confirmedStudents = activeStudents.filter(isConfirmed);
  const confirmedRiders = activeRiders.filter(isConfirmed);

  return {
    activeStudents: activeStudents.length,
    activeRiders: activeRiders.length,
    confirmedStudents: confirmedStudents.length,
    confirmedRiders: confirmedRiders.length,
    inviteLocked: !liveInvitesEnabled,
    recommendedStudentCohortSize: Math.min(confirmedStudents.length, 60),
    selectedReadyCount: selectedEntries.filter(
      (entry) => entry.status === "active" && isConfirmed(entry),
    ).length,
  };
}

export function getWaitlistInviteUrlForRole(role: WaitlistRole): string {
  return WAITLIST_INVITE_URLS[role];
}

function buildStatusUpdate(status: WaitlistStatus, adminNotes?: string): {
  status: WaitlistStatus;
  expiresAt: Date | null;
  adminNotes: string | null;
} {
  const terminalStatus = status === "activated" || status === "do_not_contact" || status === "bounced";
  const expiresAt = terminalStatus
    ? new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
    : null;

  return {
    status,
    expiresAt,
    adminNotes: adminNotes?.trim() ? adminNotes.trim() : null,
  };
}

export async function updateWaitlistStatus(
  entryId: string,
  status: WaitlistStatus,
  adminNotes?: string,
): Promise<void> {
  const payload = buildStatusUpdate(status, adminNotes);

  try {
    await updateDoc(doc(db, "waitlistEntries", entryId), {
      status: payload.status,
      expiresAt: payload.expiresAt,
      adminNotes: payload.adminNotes,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("[WaitlistService] Failed to update waitlist status:", error);
    throw new Error("Failed to update waitlist status.");
  }
}

export async function sendWaitlistInvites(
  payload: WaitlistInvitePayload,
): Promise<WaitlistInviteResult> {
  try {
    const callable = httpsCallable<WaitlistInvitePayload, WaitlistInviteResult>(
      functions,
      "sendWaitlistInvites",
    );
    const result = await callable(payload);
    return result.data;
  } catch (error) {
    console.error("[WaitlistService] Failed to send invites:", error);
    throw new Error("Failed to send waitlist invites.");
  }
}

export async function retryWaitlistConfirmation(entryId: string): Promise<void> {
  try {
    const callable = httpsCallable<{ entryId: string }, { entryId: string }>(
      functions,
      "retryWaitlistConfirmationEmail",
    );
    await callable({ entryId });
  } catch (error) {
    console.error("[WaitlistService] Failed to retry confirmation:", error);
    throw new Error("Failed to retry confirmation email.");
  }
}
